import { useState, useEffect } from 'react';
import {
	ERROR_MESSAGES,
	fetchPlaceLocation,
	geocodeLocation
} from '../utils/helper';

/**
 * Fetches the zipcode for a given Google Place ID using the PlacesService.
 * This hook is intended to be used after a user selects a location from
 * autocomplete suggestions, providing a placeId.
 *
 * @param {boolean} isApiReady - A flag indicating if the Google Maps API script has loaded.
 * @param {object | null} sessionToken - The active AutocompleteSessionToken from the search session.
 * @param {Function} resetSession - The function to reset/end the session after use.
 * @param {string | null} placeIdToFetch - The Google Place ID for which to fetch the zipcode.
 */
export const useFetchLocationGeoZip = (
	isApiReady,
	sessionToken,
	resetSession,
	placeIdToFetch
) => {
	const [zipcode, setZipcode] = useState(null);
	const [coordinates, setCoordinates] = useState(null);
	const [error, setError] = useState(null);
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		if (!placeIdToFetch || !isApiReady) {
			setZipcode(null);
			setCoordinates(null);
			setError(null);
			return;
		}

		const fetchZipcode = async () => {
			try {
				setIsLoading(true);
				// Step 1: Get coordinates from place ID
				const { lat, lng } = await fetchPlaceLocation(
					placeIdToFetch,
					sessionToken
				);

				// Store coordinates
				setCoordinates({ lat, lng });

				// Step 2: Geocode coordinates to get zipcode
				const fetchedZipcode = await geocodeLocation(lat, lng);

				setZipcode(fetchedZipcode);
				setCoordinates({ lat, lng });
				setError(null);
			} catch (err) {
				setError(err.message || ERROR_MESSAGES.FETCH_FAILED);
				setZipcode(null);
				setCoordinates(null);
			} finally {
				// Always reset session after fetch attempt
				resetSession?.();
				setIsLoading(false);
			}
		};

		fetchZipcode();
	}, [placeIdToFetch, isApiReady, sessionToken, resetSession]);

	return { zipcode, coordinates, isLoading, error };
};
